import { getAnalyticsConfig } from '@ynap/config-state'
import { formatPrice } from '@ynap/intl-formatters'
import { getCurrentShippingOption } from '@ynap/cart-state'
import getCart from './getCart'
import { getCurrentShippingAddress } from './getShippingAddressStep'

import { createCombinedSelector } from '../utils/selectorUtils'
import { Config, Locale, StoreState } from 'app/state/types'
import { Messages, ShoppingCart } from 'app/types/common-types'

export default createCombinedSelector({
    name: 'getPackagingOptionsStep',
    state: ['locale', 'config', 'cart', 'deviceType', 'isFetchingCart', 'fetchCartError'],
    props: ['messages'],
    fn: getPackagingOptionsStepInternal,
})

interface State extends StoreState {
    cart: ShoppingCart
    config: Config
    deviceType: string
    isFetchingCart?: boolean
    locale: Locale
}

type Props = {
    messages: Messages
}

type PackagingOption = {
    description: string
    key: string
    name: string
    price: string
    selected: boolean
}

type PackagingOptionReturn = {
    [key: string]: PackagingOption
}

export function getPackagingOptionsStepInternal(state: State, props: Props) {
    const { isFetchingCart, fetchCartError, config } = state
    const cart = getCart(state, props)
    const {
        lineItems,
        totals,
        genericTotal,
        firstTimeUser,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics,
        hasShippingMethodChanged,
    } = cart
    const packagingOptions = getPackagingOptions(state, props)
    const currentPackagingOption = getCurrentPackagingOption(state, props)

    const loading = isFetchingCart
    const error = fetchCartError || !packagingOptions || !packagingOptions.length
    const completed = packagingOptions ? !!currentPackagingOption : undefined
    const isGift = state.cart?.x_isGift
    const giftMessage = state.cart?.x_giftMessage
    const shippingAddress = getCurrentShippingAddress(state)
    const [shippingOption] = getCurrentShippingOption(state, props)

    return {
        packagingOptions,
        isGift,
        giftMessage,
        currentPackagingOption,
        lineItems,
        totals,
        loading,
        error,
        completed,
        analytics: getAnalyticsConfig(state),
        genericTotal,
        firstTimeUser,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics: {
            ...checkoutAnalytics,
            shippingAddress,
            shippingOption,
        },
        config,
        hasShippingMethodChanged,
    }
}

export function getPackagingOptions(state: State, props: Props) {
    const { cart = {} as ShoppingCart, locale = {} } = state
    const { orderItem } = cart
    const { messages } = props

    if (orderItem) {
        // Get packaging options from order items
        const packagingOptions = orderItem.reduce((accumulator, item) => {
            const { usablePackagingCharges = [] } = item
            usablePackagingCharges.forEach((packagingOption) => {
                const { charge, description, name, currency } = packagingOption

                accumulator[`${name}`] = {
                    key: name.toLowerCase(),
                    name,
                    description,
                    price:
                        charge.amount > 0
                            ? formatPrice(charge.amount / charge.divisor, currency.label, locale.id)
                            : messages?.packagingOptions.zeroPackagingPrice(),
                    selected: name === cart.x_packagingOption,
                }
            })
            return accumulator
        }, {})
        return values(packagingOptions)
    }
}

export function getCurrentPackagingOption(state: State, props: Props) {
    const packagingOptions = getPackagingOptions(state, props)
    if (packagingOptions) {
        const currentPackagingOption = packagingOptions.find((option) => option.selected) || {}

        return currentPackagingOption as PackagingOption
    }
}

function values(obj: PackagingOptionReturn) {
    return Object.keys(obj).map((key) => obj[key])
}
